import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api, errMsg } from "../lib/api";
import { useQueue } from "../stores/queue";
import { usePrefs } from "../stores/prefs";
import { usePermissions } from "../stores/permissions";
import { beep, vibrate } from "../lib/sound";
import { MysteryBatchModal } from "../components/stock/MysteryBatchModal";

import { TopBar } from "../components/ui/TopBar";
import { Scanner } from "../components/ui/Scanner";
import { QtyStepper } from "../components/ui/QtyStepper";
import { Field } from "../components/ui/Field";
import { Badge } from "../components/ui/Badge";
import { Icon } from "../components/ui/Icon";
import { Button } from "../components/ui/Button";
import { Banner } from "../components/ui/Banner";
import { ProductSearch } from "../components/ui/ProductSearch";

type ProductLookup = {
  sku: string;
  name: string;
  image_url: string | null;
  quantity: number;
  low_stock_threshold: number;
};

type StockOutReason = {
  id: number;
  key: string;
  label_it: string;
  requires_note: boolean;
  note_label: string | null;
  is_manual: boolean;
  active: boolean;
  sort_order: number;
};

export function StockOut() {
  const nav = useNavigate();
  const [sku, setSku] = useState("");
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [eventDisplayId, setEventDisplayId] = useState<string | null>(null);
  const [mysteryOpen, setMysteryOpen] = useState(false);

  const prefs = usePrefs((s) => s.prefs);
  const enqueue = useQueue((s) => s.enqueue);
  const { hasPermission } = usePermissions();

  const { data: product } = useQuery<ProductLookup>({
    queryKey: ["product-lookup", sku],
    queryFn: async () => (await api.get(`/api/stock/${sku}`)).data,
    enabled: !!sku && sku.length >= 3,
    retry: false,
    staleTime: 10_000,
  });

  const { data: reasons = [] } = useQuery<StockOutReason[]>({
    queryKey: ["stock-out-reasons"],
    queryFn: async () => (await api.get("/api/stock-out-reasons")).data,
    staleTime: 5 * 60_000,
  });

  const selectedReason = reasons.find((r) => r.key === reason) ?? null;
  const noteRequired = selectedReason?.requires_note ?? false;
  const noteLabel = noteRequired ? selectedReason?.note_label || "Nota" : "Note (opzionale)";
  const reasonMissing = !reason;
  const noteMissing = noteRequired && !notes.trim();

  const stockNow = product?.quantity ?? null;
  const stockAfter = stockNow !== null ? stockNow - qty : null;
  const threshold = product?.low_stock_threshold ?? prefs.low_stock_threshold;
  const willGoLow = stockAfter !== null && stockAfter < threshold;
  const wouldOverdraw = stockAfter !== null && stockAfter < 0;

  function feedback(success: boolean) {
    if (prefs.beep) beep(success ? "ok" : "warn");
    if (prefs.vibrate) vibrate(success ? 30 : [40, 30, 40]);
  }

  function onScan(code: string) {
    setSku(code.toUpperCase().trim());
    feedback(true);
  }

  async function submit() {
    if (!sku || qty <= 0 || busy) return;
    if (reasonMissing) {
      setErr("Seleziona un motivo di uscita");
      return;
    }
    if (noteMissing) {
      setErr(`Il campo '${noteLabel}' è obbligatorio per questo motivo`);
      return;
    }
    if (prefs.double_tap_stock_out && !confirming) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    const body = {
      event_type: "stock_out",
      sku,
      quantity_delta: -qty,
      reason,
      notes: notes || null,
    };
    try {
      if (!navigator.onLine) {
        enqueue({ url: "/api/stock-event", method: "POST", body });
        setMsg(`Offline — accodato (-${qty} ${sku})`);
        setEventDisplayId(null);
        resetForm();
        return;
      }
      const { data } = await api.post("/api/stock-event", body, {
        headers: { "X-Idempotency-Key": crypto.randomUUID() },
      });
      feedback(true);
      setMsg(`−${qty} ${sku} confermato`);
      setEventDisplayId(formatEventId("EV-OUT", data.id));
      resetForm();
    } catch (e) {
      feedback(false);
      setErr(errMsg(e));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  function resetForm() {
    setSku("");
    setNotes("");
    setQty(1);
    setReason("");
  }

  const cat = inferCategory(sku);

  return (
    <>
      <TopBar back onBack={() => nav(-1)} title="Stock Out" sub="Uscita straordinaria" />

      <div style={{ padding: "14px 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            background: "var(--tcg-danger-bg)",
            color: "var(--tcg-danger)",
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.06em",
          }}
        >
          USCITA · OUT
        </div>
        <span
          className="tcg-mono"
          style={{ fontSize: 10, color: "var(--tcg-muted)", fontWeight: 700, marginLeft: "auto" }}
        >
          {eventDisplayId || nextEventPlaceholder("EV-OUT")}
        </span>
      </div>

      <div style={{ padding: "0 18px" }}>
        <Scanner onDetect={onScan} paused={busy} hint="Scansiona il prodotto in uscita" />
      </div>

      <div style={{ padding: "12px 18px 0" }}>
        <ProductSearch
          selectedSku={sku}
          onPick={(p) => {
            setSku(p.sku);
            feedback(true);
          }}
        />
      </div>

      {product && (
        <div style={{ padding: "12px 18px 0" }}>
          <div
            className="tcg-card"
            style={{
              padding: 12,
              borderColor: willGoLow ? "var(--tcg-warn)" : "var(--tcg-border)",
            }}
          >
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <ScanThumb imageUrl={product.image_url} cat={cat} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, lineHeight: 1.25 }}>{product.name}</div>
                <div
                  className="tcg-mono"
                  style={{ fontSize: 10, color: "var(--tcg-muted)", marginTop: 2, fontWeight: 700 }}
                >
                  {product.sku}
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                  <Badge tone="navy">{cat.label}</Badge>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 12,
                paddingTop: 12,
                borderTop: "1px dashed var(--tcg-border)",
              }}
            >
              <div style={{ flex: 1, background: "var(--tcg-bg)", borderRadius: 10, padding: 8, textAlign: "center" }}>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: "var(--tcg-muted)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  Stock attuale
                </div>
                <div className="tcg-num" style={{ fontSize: 20, fontWeight: 900, color: "var(--tcg-ink)" }}>
                  {stockNow}
                </div>
              </div>
              <div style={{ alignSelf: "center", color: "var(--tcg-muted)", fontSize: 18, fontWeight: 700 }}>→</div>
              <div
                style={{
                  flex: 1,
                  background: wouldOverdraw
                    ? "var(--tcg-danger-bg)"
                    : willGoLow
                      ? "var(--tcg-warn-bg)"
                      : "var(--tcg-success-bg)",
                  borderRadius: 10,
                  padding: 8,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: wouldOverdraw
                      ? "var(--tcg-danger)"
                      : willGoLow
                        ? "var(--tcg-warn)"
                        : "var(--tcg-success)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  Dopo operazione
                </div>
                <div
                  className="tcg-num"
                  style={{
                    fontSize: 20,
                    fontWeight: 900,
                    color: wouldOverdraw
                      ? "var(--tcg-danger)"
                      : willGoLow
                        ? "var(--tcg-warn)"
                        : "var(--tcg-success)",
                  }}
                >
                  {stockAfter}
                </div>
              </div>
            </div>

            {wouldOverdraw && (
              <Banner tone="danger" icon="flag" style={{ marginTop: 10 }}>
                Non puoi uscire {qty} pezzi: disponibili solo {stockNow}.
              </Banner>
            )}
            {!wouldOverdraw && willGoLow && (
              <Banner tone="warn" icon="flag" style={{ marginTop: 10 }}>
                Sotto soglia minima ({threshold}). Considera un riordino al fornitore.
              </Banner>
            )}
          </div>
        </div>
      )}

      <div style={{ padding: "14px 18px 0" }}>
        <Field label="SKU">
          <input
            className="tcg-input tcg-mono"
            value={sku}
            onChange={(e) => setSku(e.target.value.toUpperCase().trim())}
            placeholder="YGO-RA01-BOX"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
          />
        </Field>

        <Field label="Quantità in uscita">
          <QtyStepper value={qty} onChange={setQty} min={1} max={Math.max(1, stockNow ?? 9999)} />
        </Field>

        <Field label="Motivo uscita (obbligatorio)">
          <select
            className="tcg-select"
            value={reason}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "produzione_mystery") {
                if (hasPermission("production_manage")) {
                  setErr(null);
                  setReason(v);
                  setMysteryOpen(true);
                } else {
                  setErr("Permesso 'Gestione produzione' richiesto per la Produzione Mystery");
                  setReason("");
                }
                return;
              }
              setReason(v);
            }}
          >
            <option value="" disabled>
              Seleziona un motivo…
            </option>
            {reasons.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label_it}
              </option>
            ))}
          </select>
        </Field>

        <Field label={noteRequired ? `${noteLabel} (obbligatorio)` : noteLabel}>
          <textarea
            className="tcg-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              noteRequired
                ? `Inserisci ${noteLabel.toLowerCase()}…`
                : "Es. cliente walk-in, evento Lucca, motivo del danno…"
            }
          />
        </Field>

        {err && (
          <Banner tone="danger" icon="x">
            {err}
          </Banner>
        )}
        {msg && !err && (
          <Banner tone="success" icon="check">
            {msg}
          </Banner>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <Button
            variant="secondary"
            onClick={() => {
              if (confirming) setConfirming(false);
              else nav(-1);
            }}
            disabled={busy}
          >
            {confirming ? "Annulla" : "Indietro"}
          </Button>
          <Button
            variant="danger"
            style={{ flex: 1 }}
            disabled={!sku || busy || wouldOverdraw || reasonMissing || noteMissing}
            onClick={submit}
          >
            <Icon name="out" size={16} />
            {busy
              ? "Conferma…"
              : confirming
                ? `Tocca ancora · −${qty}`
                : `Conferma uscita · −${qty}`}
          </Button>
        </div>
      </div>

      <div style={{ height: 24 }} />

      <MysteryBatchModal
        open={mysteryOpen}
        onClose={() => {
          setMysteryOpen(false);
          setReason("");
        }}
        onDone={(summary) => {
          setErr(null);
          setMsg(summary);
          setEventDisplayId(null);
        }}
      />
    </>
  );
}

function ScanThumb({ imageUrl, cat }: { imageUrl: string | null; cat: CategoryMeta }) {
  const [errored, setErrored] = useState(false);
  const showImg = imageUrl && !errored;
  return (
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 11,
        background: showImg
          ? "var(--tcg-bg)"
          : cat.thumbBg || "repeating-linear-gradient(135deg, #E8E4D9 0 8px, #D4CFC2 8px 16px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        overflow: "hidden",
        color: cat.thumbFg,
        fontSize: 9,
        fontWeight: 800,
        textAlign: "center",
        lineHeight: 1.1,
      }}
    >
      {showImg ? (
        <img
          src={imageUrl!}
          alt=""
          loading="lazy"
          onError={() => setErrored(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        cat.thumbLabel
      )}
    </div>
  );
}

function formatEventId(prefix: string, id: number): string {
  return `${prefix}-${String(id).padStart(4, "0")}`;
}

function nextEventPlaceholder(prefix: string): string {
  return `${prefix}-····`;
}

type CategoryMeta = {
  label: string;
  thumbBg: string | null;
  thumbFg: string;
  thumbLabel: string;
};

function inferCategory(sku: string): CategoryMeta {
  const first = sku.split("-")[0]?.toUpperCase() || "";
  switch (first) {
    case "POK":
      return { label: "Pokémon", thumbBg: "repeating-linear-gradient(135deg, #FFE4D6 0 8px, #FFD4BA 8px 16px)", thumbFg: "#9A3412", thumbLabel: "POK" };
    case "OP":
      return { label: "One Piece", thumbBg: "repeating-linear-gradient(135deg, #DBEAFE 0 8px, #BFDBFE 8px 16px)", thumbFg: "#1E40AF", thumbLabel: "OP" };
    case "YGO":
      return { label: "Yu-Gi-Oh!", thumbBg: "repeating-linear-gradient(135deg, #FCE7F3 0 8px, #FBCFE8 8px 16px)", thumbFg: "#9D174D", thumbLabel: "YGO" };
    case "LOR":
      return { label: "Lorcana", thumbBg: "repeating-linear-gradient(135deg, #FFEDD5 0 8px, #FED7AA 8px 16px)", thumbFg: "#C2410C", thumbLabel: "LOR" };
    case "FUN":
      return { label: "Funko", thumbBg: "repeating-linear-gradient(135deg, #CCFBF1 0 8px, #99F6E4 8px 16px)", thumbFg: "#0F766E", thumbLabel: "FUN" };
    case "DB":
      return { label: "Dragon Ball", thumbBg: "repeating-linear-gradient(135deg, #FEF3C7 0 8px, #FDE68A 8px 16px)", thumbFg: "#92400E", thumbLabel: "DB" };
    case "MTG":
      return { label: "Magic", thumbBg: "repeating-linear-gradient(135deg, #E6EBF8 0 8px, #C7D2FE 8px 16px)", thumbFg: "#1B3A8C", thumbLabel: "MTG" };
    default:
      return { label: "Altro", thumbBg: null, thumbFg: "#3E4255", thumbLabel: first.slice(0, 3) || "???" };
  }
}
